/**
 * serverDisplay -- Centralized server-type display helpers.
 *
 * Provides label, brand color, icon, and Tailwind color class lookups for
 * all supported media server types (Emby, Jellyfin, Plex). Eliminates the
 * ~57 scattered `serverType === "emby" ? ... : "Jellyfin"` ternaries across
 * WSD components and ensures Plex renders correctly everywhere.
 *
 * Consumers:
 *   - All streaming route pages (MediaItemPage, DevicesPage, UserDetailPage)
 *   - All streaming components (SessionCard, UserAdminPanel, UserDrawer, etc.)
 *   - Control-center domains and media sub-components
 */

// ============================================================================
// Types
// ============================================================================

export type ServerTypeForDisplay = "emby" | "jellyfin" | "plex";

// ============================================================================
// Label map
// ============================================================================

export const SERVER_LABELS: Record<ServerTypeForDisplay, string> = {
  emby: "Emby",
  jellyfin: "Jellyfin",
  plex: "Plex",
};

// ============================================================================
// Brand color map (hex, for inline styles / canvas)
// ============================================================================

export const SERVER_BRAND_COLORS: Record<ServerTypeForDisplay, string> = {
  emby: "#52b54b",
  jellyfin: "#aa5cc3",
  plex: "#e5a00d",
};

// ============================================================================
// Iconify icon name map
// ============================================================================

export const SERVER_ICONS: Record<ServerTypeForDisplay, string> = {
  emby: "simple-icons:emby",
  jellyfin: "simple-icons:jellyfin",
  // simple-icons:plex is available in the icon set used by the project
  plex: "simple-icons:plex",
};

// ============================================================================
// Tailwind text-color class map
// These match the existing Emby (emerald) and Jellyfin (purple) conventions.
// Plex uses amber to match the #e5a00d brand color.
// ============================================================================

export const SERVER_COLOR_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-500",
  jellyfin: "text-purple-500",
  plex: "text-amber-500",
};

/** Slightly dimmer variant for secondary / decorative icon usage */
export const SERVER_COLOR_CLASSES_MUTED: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400",
  jellyfin: "text-purple-400",
  plex: "text-amber-400",
};

/** Dimmed variant for 50-70% opacity contexts (e.g., source-list entries) */
export const SERVER_COLOR_CLASSES_DIM: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-500/70",
  jellyfin: "text-purple-500/70",
  plex: "text-amber-500/70",
};

/** 60% dimmed variant used in item panels */
export const SERVER_COLOR_CLASSES_60: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400/60",
  jellyfin: "text-purple-400/60",
  plex: "text-amber-400/60",
};

/** 50% dimmed variant used in library shelf entries */
export const SERVER_COLOR_CLASSES_50: Record<ServerTypeForDisplay, string> = {
  emby: "text-emerald-400/50",
  jellyfin: "text-purple-400/50",
  plex: "text-amber-400/50",
};

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Get the display label for a server type.
 * Falls back to the raw value if not in the registry (forward-compat).
 */
export function getServerLabel(serverType: string | undefined | null): string {
  if (!serverType) return "Unknown";
  return SERVER_LABELS[serverType as ServerTypeForDisplay] ?? serverType;
}

/**
 * Get the brand hex color for a server type (for inline styles, canvas, etc.).
 */
export function getServerBrandColor(serverType: string | undefined | null): string {
  if (!serverType) return "#888";
  return SERVER_BRAND_COLORS[serverType as ServerTypeForDisplay] ?? "#888";
}

/**
 * Get the Iconify icon name for a server type.
 */
export function getServerIcon(serverType: string | undefined | null): string {
  if (!serverType) return "lucide:server";
  return SERVER_ICONS[serverType as ServerTypeForDisplay] ?? "lucide:server";
}

/**
 * Get the Tailwind text-color class for a server type (full / primary usage).
 */
export function getServerColorClass(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the muted Tailwind text-color class (decorative / secondary icon usage).
 */
export function getServerColorClassMuted(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_MUTED[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the dimmed (70%) Tailwind text-color class.
 */
export function getServerColorClassDim(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_DIM[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the 60%-opacity Tailwind text-color class (used in item device panels).
 */
export function getServerColorClass60(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_60[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

/**
 * Get the 50%-opacity Tailwind text-color class (used in library shelf entries).
 */
export function getServerColorClass50(serverType: string | undefined | null): string {
  if (!serverType) return "text-muted-foreground";
  return SERVER_COLOR_CLASSES_50[serverType as ServerTypeForDisplay] ?? "text-muted-foreground";
}

// ============================================================================
// Avatar classes (combined `bg-X-500/15 text-X-400` for avatar circles)
// ============================================================================

const SERVER_AVATAR_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "bg-emerald-500/15 text-emerald-400",
  jellyfin: "bg-purple-500/15 text-purple-400",
  plex: "bg-amber-500/15 text-amber-400",
};

/**
 * Get the combined avatar Tailwind classes (background + text) for a server type.
 * Used for user avatar circles and similar paired bg/text elements.
 */
export function getServerAvatarClasses(serverType: string | undefined | null): string {
  if (!serverType) return "bg-muted text-muted-foreground";
  return SERVER_AVATAR_CLASSES[serverType as ServerTypeForDisplay] ?? "bg-muted text-muted-foreground";
}

// ============================================================================
// Background color map (for server badge circles, etc.)
// ============================================================================

const SERVER_BG_CLASSES: Record<ServerTypeForDisplay, string> = {
  emby: "bg-emerald-500",
  jellyfin: "bg-purple-500",
  plex: "bg-amber-500",
};

/**
 * Get the Tailwind background-color class for a server type.
 * Used for server badge circles, avatar backgrounds, etc.
 */
export function getServerBgClass(serverType: string | undefined | null): string {
  if (!serverType) return "bg-muted";
  return SERVER_BG_CLASSES[serverType as ServerTypeForDisplay] ?? "bg-muted";
}

// ============================================================================
// Deep-link URL builder
// ============================================================================

/**
 * Build the deep-link URL into a server's web UI for a specific item.
 *
 * - Emby:     `{base}/web/index.html#!/item?id={itemId}&serverId={serverId}`
 * - Jellyfin: `{base}/web/index.html#!/details?id={itemId}&serverId={serverId}`
 * - Plex:     `{base}/web/index.html#!/server/{machineId}/details?key={encoded /library/metadata/itemId}`
 *
 * @param serverType  - The media server type ("emby" | "jellyfin" | "plex")
 * @param baseUrl     - The server base URL (no trailing slash)
 * @param itemId      - The item's ID on the media server
 * @param serverId    - For Emby/Jellyfin: the `serverId` query param;
 *                      for Plex: the machine identifier (passed as `machineId`)
 */
export function buildServerItemUrl(args: {
  serverType: string | undefined | null;
  baseUrl: string;
  itemId: string;
  serverId?: string | null;
}): string | null {
  const { serverType, baseUrl, itemId, serverId } = args;
  if (!serverType || !baseUrl || !itemId) return null;

  const base = baseUrl.replace(/\/+$/, "");

  if (serverType === "plex") {
    // Plex deep-link requires the machine identifier in the path.
    // serverId carries the machineIdentifier populated by the Plex adapter.
    if (!serverId) return null;
    const key = encodeURIComponent(`/library/metadata/${itemId}`);
    return `${base}/web/index.html#!/server/${serverId}/details?key=${key}`;
  }

  const serverIdParam = serverId ? `&serverId=${encodeURIComponent(serverId)}` : "";

  if (serverType === "emby") {
    return `${base}/web/index.html#!/item?id=${encodeURIComponent(itemId)}${serverIdParam}`;
  }

  // Default: Jellyfin
  return `${base}/web/index.html#!/details?id=${encodeURIComponent(itemId)}${serverIdParam}`;
}
